import { useOnFailureRedirectPath } from '#src/clientState/hooks';
import { isACompleteUrl } from '#src/clientState/hooks/useShoppingBasketPaymentPage/useShoppingBasketPaymentPage.utils';
import {
  DATA_MESH_IDS,
  hideWaitingPage,
  navigateToPath,
  useStylePerFlow,
} from '#src/utils';
import {
  useTrackPage,
  useTracking,
  useTranslation,
} from '@edreamsodigeo/frontend-core';
import {
  Body,
  Box,
  Button,
  Glyph,
  Heading,
  SystemModal,
  SystemModalContent,
  SystemModalHeader,
  SystemModalIcon,
} from '@edreamsodigeo/prisma-design-system';
import React, { useEffect } from 'react';

import { DatameshTracking } from '../Common/DatameshTracking';
import { GO_BACK_EVENT, ONLOAD_EVENT, PAGE_NAME, RETRY_EVENT } from './utils';

type ErrorModalProps = {
  isOpen: boolean;
  close: () => void;
  onRetry: () => void;
};

export const navigateBack = (onFailureRedirectPath: string) => {
  if (isACompleteUrl(onFailureRedirectPath)) {
    throw new Error(
      `onFailureRedirectPath is a complete URL, not a path, ${onFailureRedirectPath}`
    );
  }
  navigateToPath(onFailureRedirectPath);
};

export const ErrorModal = ({ isOpen, close, onRetry }: ErrorModalProps) => {
  const { t } = useTranslation();
  const onFailureRedirectPath = useOnFailureRedirectPath();

  const getStyle = useStylePerFlow();
  const { trackEvent } = useTracking();
  const { trackGAPage } = useTrackPage();

  useEffect(() => {
    if (isOpen) {
      trackGAPage(PAGE_NAME);
      hideWaitingPage();
    }
  }, [isOpen, trackGAPage]);

  const handleRetryClick = () => {
    trackEvent(RETRY_EVENT);
    onRetry();
  };

  const handleClose = () => {
    if (close) close();
    trackEvent(GO_BACK_EVENT);
    navigateBack(onFailureRedirectPath);
  };

  useEffect(() => {
    if (isOpen) {
      trackEvent(ONLOAD_EVENT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <SystemModal
      id="universal-payment-page-error-modal"
      modalSize="medium"
      onClose={handleClose}
      open={isOpen}
    >
      <SystemModalHeader type="warning">
        <SystemModalIcon>
          <Glyph category="Alerts" color="warning.50" name="Warning" />
        </SystemModalIcon>
      </SystemModalHeader>
      <SystemModalContent>
        <Box
          pb={getStyle({ desktop: 'base', mobile: '1x_small' })}
          px={getStyle({ desktop: '1x_big', mobile: 'tiny' })}
        >
          <Box pb={getStyle({ desktop: 'base', mobile: 'big' })}>
            <Heading color="neutral.90" textAlign="center" type="2">
              {t('errormodal.generic.title')}
            </Heading>
          </Box>
          <Box pb={getStyle({ desktop: '1x_big', mobile: 'big' })}>
            <Body color="neutral.80" textAlign="center" type="base">
              {t('errormodal.generic.description')}
            </Body>
          </Box>

          <Box pb={'base'}>
            <DatameshTracking click datameshId={DATA_MESH_IDS.ERROR_MODAL} load>
              <Button fullWidth onClick={handleClose} type="primary">
                {t('errormodal.generic.continue_btn')}
              </Button>
            </DatameshTracking>
          </Box>
          <DatameshTracking
            click
            datameshId={DATA_MESH_IDS.ERROR_MODAL_TRY_AGAIN}
          >
            <Button fullWidth type="secondary" onClick={handleRetryClick}>
              {t('errormodal.generic.try_again_btn')}
            </Button>
          </DatameshTracking>
        </Box>
      </SystemModalContent>
    </SystemModal>
  );
};
